import React, { useState } from 'react';
import styled from 'styled-components';
import { Button } from '@mui/material';

const DashboardCalculator = ({ onEstimateComplete }) => {
  const [formData, setFormData] = useState({
    projectType: 'outdoor-kitchen',
    length: '',
    width: '',
    height: '',
    zipCode: ''
  });

  const [estimateResult, setEstimateResult] = useState(null);
  const [showEstimate, setShowEstimate] = useState(false);

  const calculateEstimate = () => {
    let total = 0;
    let squareFootageCalc = 0;

    switch (formData.projectType) {
      case 'outdoor-kitchen':
        // Outdoor kitchen only needs length
        total = parseFloat(formData.length) * 825; // $825 per linear foot
        squareFootageCalc = parseFloat(formData.length) * 2.67; // Standard 32" depth
        break;

      case 'retaining-wall':
        // Retaining wall needs length and height
        const wallHeight = parseFloat(formData.height) + 1; // Add 1ft below grade
        total = parseFloat(formData.length) * wallHeight * 25;
        squareFootageCalc = parseFloat(formData.length) * wallHeight;
        break;

      case 'concrete-patio':
        // Patio needs length and width
        total = parseFloat(formData.length) * parseFloat(formData.width) * 10;
        squareFootageCalc = parseFloat(formData.length) * parseFloat(formData.width);
        break;

      case 'pergola':
        // Pergola needs length and width
        total = parseFloat(formData.length) * parseFloat(formData.width) * 40;
        squareFootageCalc = parseFloat(formData.length) * parseFloat(formData.width);
        break;

      default:
        total = 0;
        squareFootageCalc = 0;
    }

    return { total, squareFootageCalc };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { total, squareFootageCalc } = calculateEstimate();

    const estimateData = {
      amount: total,
      projectDetails: {
        service: formData.projectType,
        dimensions: `${formData.length}' x ${formData.width}'`,
        area: `${squareFootageCalc.toFixed(2)} sq ft`,
        zipCode: formData.zipCode
      }
    };

    setEstimateResult(estimateData);
    setShowEstimate(true);
  };

  const handleAccept = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');

      const response = await fetch('http://localhost:5001/api/estimates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId,
          ...estimateResult
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save estimate');
      }

      // Clear form and hide estimate
      setFormData({
        projectType: 'outdoor-kitchen',
        length: '',
        width: '',
        height: '',
        zipCode: ''
      });
      setShowEstimate(false);
      setEstimateResult(null);

      // Notify parent to refresh estimates
      if (onEstimateComplete) {
        onEstimateComplete();
      }
    } catch (error) {
      console.error('Error saving estimate:', error);
    }
  };

  const handleDecline = () => {
    setShowEstimate(false);
    setEstimateResult(null);
  };

  if (showEstimate && estimateResult) {
    return (
      <EstimatePreview>
        <Title>Estimate Preview</Title>
        <Amount>${estimateResult.amount.toLocaleString()}</Amount>
        <Details>
          <DetailRow>
            <Label>Service:</Label>
            <Value>
              {formData.projectType.split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')}
            </Value>
          </DetailRow>
          <DetailRow>
            <Label>Dimensions:</Label>
            <Value>{estimateResult.projectDetails.dimensions}</Value>
          </DetailRow>
          <DetailRow>
            <Label>Area:</Label>
            <Value>{estimateResult.projectDetails.area}</Value>
          </DetailRow>
        </Details>
        <Actions>
          <AcceptButton onClick={handleAccept}>Accept</AcceptButton>
          <DeclineButton onClick={handleDecline}>Decline</DeclineButton>
        </Actions>
      </EstimatePreview>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Select
        value={formData.projectType}
        onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
      >
        <option value="outdoor-kitchen">Outdoor Kitchen</option>
        <option value="retaining-wall">Retaining Wall</option>
        <option value="concrete-patio">Concrete Patio</option>
        <option value="pergola">Pergola</option>
      </Select>

      <Input
        type="number"
        placeholder="Length (ft)"
        value={formData.length}
        onChange={(e) => setFormData({ ...formData, length: e.target.value })}
        required
      />

      {/* Only show width for patio and pergola */}
      {(formData.projectType === 'concrete-patio' || formData.projectType === 'pergola') && (
        <Input
          type="number"
          placeholder="Width (ft)"
          value={formData.width}
          onChange={(e) => setFormData({ ...formData, width: e.target.value })}
          required
        />
      )}

      {/* Only show height for retaining wall */}
      {formData.projectType === 'retaining-wall' && (
        <Input
          type="number"
          placeholder="Height (ft)"
          value={formData.height}
          onChange={(e) => setFormData({ ...formData, height: e.target.value })}
          required
        />
      )}

      <Input
        type="text"
        placeholder="ZIP Code"
        value={formData.zipCode}
        onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
        required
        pattern="[0-9]{5}"
      />

      <SubmitButton type="submit">Calculate Estimate</SubmitButton>
    </Form>
  );
};

// Styled components
const Form = styled.form`
  padding: 2rem;
  border-radius: 16px;
  background: white;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  margin-bottom: 1.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  margin-bottom: 1.5rem;
`;

const SubmitButton = styled(Button)`
  && {
    background: #FF5722;
    color: white;
    padding: 0.8rem 2rem;
    font-size: 1.1rem;
    width: 100%;
    
    &:hover {
      background: #F4511E;
    }
  }
`;

const EstimatePreview = styled.div`
  padding: 2rem;
  border-radius: 16px;
  background: white;
`;

const Title = styled.h2`
  color: #333;
  margin-bottom: 1.5rem;
`;

const Amount = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #FF5722;
  margin-bottom: 2rem;
`;

const Details = styled.div`
  margin-bottom: 2rem;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const Label = styled.span`
  color: #666;
`;

const Value = styled.span`
  color: #333;
  font-weight: 500;
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
`;

const AcceptButton = styled(Button)`
  && {
    background: #4CAF50;
    color: white;
    padding: 0.8rem 2rem;
    flex: 1;
    
    &:hover {
      background: #388E3C;
    }
  }
`;

const DeclineButton = styled(Button)`
  && {
    border: 1px solid #D32F2F;
    color: #D32F2F;
    padding: 0.8rem 2rem;
    flex: 1;
    
    &:hover {
      background: #FFEBEE;
    }
  }
`;

export default DashboardCalculator; 